import { ITALY, UAE } from './content/italydubai.ts';
import { NPC_KIN } from './kin.ts';
import { LAGOS } from './content/lagos.ts';
import { FRANCE } from './content/france.ts';
import { CHINA } from './content/china.ts';
import { JAPAN } from './content/japan.ts';
import { US } from './content/us.ts';
import { EDU_RANK, SKILLS, type EduLevel, type Job, type NameGroup, type RegionContent, type Sex, type Skill, type Stage } from './content/types.ts';
import { NAMES } from './content/names.ts';
import { newSeed, Rng } from './rng.ts';

/**
 * Spawn: "spawn a situation, not a person" (PLAN.md). Given who the player made (sex, age,
 * life stage, region), generate the circumstances they start in. Weighted and correlated, never
 * plain dice: background drives schooling, money and housing; schooling and field drive jobs;
 * jobs and age drive skills; the opportunity comes from the whole picture.
 * Deterministic: the same seed gives the same life, so a reroll is just a new seed.
 */

export const REGIONS: Record<string, RegionContent> = { lagos: LAGOS, france: FRANCE, us: US, china: CHINA, japan: JAPAN, italy: ITALY, uae: UAE };

export const STAGES: Record<Stage, { label: string; min: number; max: number; blurb: string }> = {
  young: { label: 'Young adult', min: 18, max: 25, blurb: 'Your life is just beginning.' },
  adult: { label: 'Adult', min: 26, max: 40, blurb: 'You already have a history.' },
};

export interface LifeInput { region: string; stage: Stage; sex: Sex; age: number; name?: string; seed: number; /** testing only: force the family background ('struggling' | 'working' | 'comfortable' | 'wealthy') */ background?: string; /** the traits the player picked (one end of an axis each; the rest are rolled) */ traits?: string[] }

export interface Relation { relation: string; name: string; note?: string }

export interface LifeCard {
  seed: number;
  region: string;
  city: string;
  currency: { symbol: string; code: string; rate?: number };
  stage: Stage;
  sex: Sex;
  age: number;
  name: string;
  background: { id: string; label: string; family: string };
  education: { level: EduLevel; label: string; field: string | null; place: string | null };
  job: { id: string; label: string; place: string; incomeMonthly: number } | null;
  money: number;
  home: { id: string; label: string; area: string; rentYearly: number; rentDueMonths: number; note: string };
  skills: Record<Skill, number>;
  personality: string[];
  people: Relation[];
  health: number;
  opportunity: string;
}

/** Personality axes: each person leans to one end of three of them. Traits are gravity, not rules:
 * they shape what the world offers you, and they drift with what you actually do (see character.ts). */
export const TRAIT_AXES: [string, string][] = [['ambitious', 'easygoing'], ['outgoing', 'introverted'], ['disciplined', 'impulsive'], ['creative', 'practical'], ['kind', 'selfish'], ['honest', 'cunning'], ['cautious', 'reckless']];
const TRAIT_SKILLS: Record<string, Partial<Record<Skill, number>>> = {
  outgoing: { social: 12 }, introverted: { academics: 6 }, disciplined: { fitness: 6, academics: 4 }, impulsive: { social: 4 },
  creative: { creativity: 12, music: 4 }, practical: { technical: 6, business: 4 }, cunning: { business: 4, social: 4 }, ambitious: { business: 6 }, cautious: { academics: 4 },
};

const round = (n: number, to: number) => Math.round(n / to) * to;

/**
 * A Lagos wage, made livable: under ₦100,000 a month up 30% (food and the ride to work were most of it), and an
 * apprentice's ₦0 a stipend of ₦25,000. Other countries' wages are already their own.
 */
export function fairPay(n: number, jobId = ''): number {
  if (jobId.startsWith('apprentice')) return Math.max(25_000, n < 100_000 ? Math.round((n * 1.3) / 1000) * 1000 : n);
  return n > 0 && n < 100_000 ? Math.round((n * 1.3) / 1000) * 1000 : n;
}

export function regionFor(id: string): RegionContent | null { return REGIONS[id] ?? null; }

export function randomName(c: RegionContent, sex: Sex, rng: Rng, group = rng.weighted(c.names.map((g) => [g, g.weight] as const))): string {
  return group.surnameFirst ? `${rng.pick(group.surname)} ${rng.pick(group[sex])}` : `${rng.pick(group[sex])} ${rng.pick(group.surname)}`;
}

/** The naming tradition a name belongs to (by surname, then given name), else a weighted pick. */
function groupOf(c: RegionContent, name: string, rng: Rng): NameGroup {
  const parts = name.trim().split(/\s+/);
  const last = parts[parts.length - 1], first = parts[0];
  return c.names.find((g) => g.surname.includes(g.surnameFirst ? first : last)) ?? c.names.find((g) => g.m.includes(first) || g.f.includes(first))
    ?? rng.weighted(c.names.map((g) => [g, g.weight] as const));
}

/** A plausible name for anyone spawning in a region (falls back to Lagos names). */
export function nameFor(region: string, sex: Sex, rng = new Rng(newSeed())): string {
  const full = REGIONS[region];
  if (full) return randomName(full, sex, rng);
  const n = NAMES[region];
  if (!n) return randomName(LAGOS, sex, rng);
  const given = rng.pick(n[sex]), family = rng.pick(n.surname);
  return n.surnameFirst ? `${family} ${given}` : `${given} ${family}`;
}

/** What a level of schooling is called where you are ("BSc Medicine", "Licence in Medicine", "BA/BS in Medicine"). */
export function eduLabelFor(cid: string, level: EduLevel, fieldLabel: string | null): string {
  const c = { id: cid }, field = fieldLabel ? { label: fieldLabel } : null;
  return {
    secondary: c.id === 'france' ? 'Baccalauréat' : c.id === 'us' || c.id === 'italy' || c.id === 'uae' ? 'High school diploma' : c.id === 'china' ? 'High school (gaokao)' : c.id === 'japan' ? 'High school' : 'Secondary school (WAEC)',
    student: `${field?.label} student`,
    diploma: c.id === 'france' ? `BTS in ${field?.label}` : c.id === 'us' || c.id === 'italy' || c.id === 'uae' ? `Associate degree in ${field?.label}` : c.id === 'china' ? `Vocational diploma in ${field?.label}` : c.id === 'japan' ? `Vocational diploma in ${field?.label}` : `ND/HND in ${field?.label}`,
    degree: c.id === 'france' ? `Licence in ${field?.label}` : c.id === 'us' || c.id === 'italy' || c.id === 'uae' ? `BA/BS in ${field?.label}` : c.id === 'china' || c.id === 'japan' ? `Bachelor's in ${field?.label}` : `BSc ${field?.label}`,
    masters: c.id === 'france' ? `Master in ${field?.label}` : c.id === 'us' || c.id === 'italy' || c.id === 'uae' || c.id === 'china' || c.id === 'japan' ? `Master's in ${field?.label}` : `MSc ${field?.label}`,
  }[level];
}

export function generateLife(input: LifeInput): LifeCard {
  const c = regionFor(input.region) ?? LAGOS;
  const rng = new Rng(input.seed);
  const { stage, sex, age } = input;

  // background
  const bgRoll = rng.weighted(c.backgrounds.map((b) => [b, b.weight] as const));
  const bg = (input.background && c.backgrounds.find((b) => b.id === input.background)) || bgRoll;

  // education: correlated with age and how much the family could invest in schooling
  const s = bg.schooling;
  let level: EduLevel;
  if (stage === 'young') {
    if (age <= 19) level = rng.chance(s * 0.85) ? 'student' : 'secondary';
    else if (age <= 21) level = rng.weighted([['student', s], ['diploma', 0.2], ['secondary', 1 - s]]);
    else level = rng.weighted([['degree', s * 0.8], ['student', age <= 23 ? 0.15 : 0.04], ['diploma', 0.2], ['secondary', 1 - s]]);
  } else {
    level = rng.weighted([['masters', s * 0.15], ['degree', s * 0.7], ['diploma', 0.2], ['secondary', (1 - s) * 0.9]]);
  }
  const field = level === 'secondary' ? null : rng.weighted(c.fields.map((f) => [f, f.id === 'medicine' ? 0.4 : 1] as const));
  const schools = level === 'student' ? c.institutions.student : level === 'diploma' ? c.institutions.diploma : c.institutions.degree;
  // private universities are for families who can pay the fees
  const affordable = schools.filter((n) => !c.institutions.private?.includes(n) || bg.schooling >= 0.8);
  let place = level === 'secondary' ? null : rng.pick(affordable.length ? affordable : schools);
  const eduLabel = eduLabelFor(c.id, level, field?.label ?? null);

  // personality: three traits on different axes
  const picked = (input.traits ?? []).filter((t, i, a) => a.indexOf(t) === i && TRAIT_AXES.some((p) => p.includes(t)));
  const chosen = picked.filter((t, i) => !picked.slice(0, i).some((o) => TRAIT_AXES.some((p) => p.includes(o) && p.includes(t)))).slice(0, 3);
  const personality = chosen.length
    ? [...chosen, ...rng.sample(TRAIT_AXES.filter((p) => !p.some((t) => chosen.includes(t))), 3 - chosen.length).map((pair) => rng.pick(pair))]
    : rng.sample(TRAIT_AXES, 3).map((pair) => rng.pick(pair));

  // job: what this person could plausibly be doing
  const rank = EDU_RANK[level];
  const fits = (j: Job) => j.stages.includes(stage) && EDU_RANK[j.minEdu] <= rank && (!j.fields || (field && j.fields.includes(field.id))) && (!j.sex || j.sex === sex)
    && !(j.id === 'nysc' && (level !== 'degree' || age > 25)) && !(j.id === 'intern' && level === 'secondary');
  const tops = c.jobs.map((j) => j.income[1]).filter((v) => v > 0).sort((a, b) => a - b);
  const payRank = (v: number) => tops.filter((t) => t < v).length / Math.max(1, tops.length - 1);
  const jobWeight = (j: Job) => {
    let w = j.weight * (j.bg?.[bg.id] ?? 1);
    if (j.id === 'nysc') w *= 3; // most young graduates are serving
    if (j.fields && rank >= 2) w *= 2; // a degree pulls you towards its profession
    if (rank >= 2 && (j.minEdu === 'secondary')) w *= 0.35;
    // family money shapes the work you end up in: a rich kid with a rich dad doesn't run a POS kiosk,
    // and a struggling one rarely walks into the best-paid jobs. Judged against this region's own pay
    // (where the job's top pay ranks among all its jobs), so it works in any currency.
    if (j.income[1] > 0) {
      const q = payRank(j.income[1]);
      if (bg.id === 'wealthy') w *= q < 0.4 ? 0 : q < 0.6 ? 0.3 : 1.8;
      else if (bg.id === 'comfortable') w *= q < 0.2 ? 0.1 : q < 0.4 ? 0.6 : 1.2;
      else if (bg.id === 'struggling' && q > 0.85) w *= 0.4;
    }
    return w;
  };
  let job: Job | null = null;
  // students mostly just study; some work on the side
  const pool = c.jobs.filter((j) => fits(j) && (level !== 'student' || (j.minEdu !== 'degree' && j.id !== 'none')));
  // (nothing plausible at all, e.g. a rich student's side jobs all weigh zero: then no job, not the first in the list)
  const weighed = pool.map((j) => [j, jobWeight(j)] as const).filter(([, w]) => w > 0);
  job = (level !== 'student' || rng.chance(0.3)) && weighed.length ? rng.weighted(weighed) : null;
  const income0 = job ? round(rng.range(job.income[0], job.income[1]), 1000) : 0;
  const income = job && c.id === 'lagos' ? fairPay(income0, job.id) : income0;

  // skills: a believable spread, shaped by schooling, field, personality, job and years
  const skills = {} as Record<Skill, number>;
  for (const k of SKILLS) skills[k] = 6 + rng.bell() * 24;
  skills.academics += rank * 9;
  const fieldScale = { secondary: 0, student: 0.6, diploma: 0.8, degree: 1, masters: 1.25 }[level];
  for (const [k, v] of Object.entries(field?.skills ?? {})) skills[k as Skill] += v * fieldScale;
  for (const t of personality) for (const [k, v] of Object.entries(TRAIT_SKILLS[t] ?? {})) skills[k as Skill] += v;
  const years = Math.max(0, age - (rank >= 2 ? 23 : 18));
  for (const [k, v] of Object.entries(job?.skills ?? {})) skills[k as Skill] += v * Math.min(1.4, 0.45 + years * 0.08);
  for (const k of SKILLS) skills[k] = Math.round(Math.min(92, Math.max(2, skills[k] + rng.range(-4, 4))));

  // money: the family's cushion for young adults, mostly your own savings for adults
  const famMoney = rng.range(bg.money[0], bg.money[1]);
  let money = stage === 'young' ? famMoney + income * rng.range(0, 1.2) : famMoney * 0.4 + income * rng.range(0.3, 4);
  if (job?.id === 'failed_biz') money *= 0.3;
  if (personality.includes('impulsive')) money *= 0.7;
  if (personality.includes('disciplined')) money *= 1.25;
  money = round(money, 500);
  // testing only: a forced rich life starts with ₦30m on top (in France, €100k), enough to travel
  if (input.background === 'wealthy') money += c.id === 'lagos' ? 30_000_000 : 100_000 * (c.currency.rate ?? 1);

  // home: with family, or renting what the income allows (Lagos rent is yearly, up front)
  let homeId: string;
  const famHome = rng.weighted(bg.homes);
  const tiers = c.homeTiers ?? { oneBed: 380_000, studio: 130_000, youngMoveOut: 250_000, youngOneBed: 600_000, cheapest: 'face_me', richAlone: 300_000 };
  const isFamily = (id: string) => id.startsWith('family');
  if (stage === 'young') {
    // young adults live at home unless they earn enough to move out (a family may pay for a
    // rented place, but only once you're 21+)
    const famOnly = isFamily(famHome) || age < 21 ? (bg.homes.find(([id]) => isFamily(id))?.[0] ?? 'family_rented') : famHome;
    homeId = income > tiers.youngMoveOut && age >= 22 && rng.chance(0.6) ? (income > tiers.youngOneBed ? 'mini_flat' : 'self_contain') : famOnly;
  } else {
    const stays = isFamily(famHome) && rng.chance(bg.id === 'wealthy' ? 0.35 : bg.id === 'comfortable' ? 0.2 : 0.1);
    homeId = stays ? famHome : bg.id === 'wealthy' && income < tiers.richAlone ? 'lekki_flat'
      : income >= tiers.oneBed ? 'mini_flat' : income >= tiers.studio ? 'self_contain' : tiers.cheapest;
  }
  let h = c.homes.find((x) => x.id === homeId)!;
  const nearHome = (places: string[], area: string) => {
    const g = c.areaGroups?.find((re) => re.test(area));
    const near = g ? places.filter((p) => g.test(p)) : [];
    return near.length ? near : places;
  };
  let area = rng.pick(h.area);
  // you live where you work: a job that only exists in some places (the orchard by the lake, the Wall) puts your home there;
  // if this kind of home doesn't exist there, you live with family there
  if (job && c.areaGroups) {
    const grp = (a: string) => c.areaGroups!.find((re) => re.test(a));
    const fits = (a: string) => { const ga = grp(a); return !!ga && job.place.some((p) => ga.test(p)); };
    const gh = grp(area);
    if (gh && !job.place.some((p) => gh.test(p))) {
      let fit = h.area.filter(fits);
      if (!fit.length) { const fam = c.homes.find((x) => x.id.startsWith('family') && x.area.some(fits)); if (fam) { h = fam; fit = fam.area.filter(fits); } }
      if (fit.length) area = rng.pick(fit);
    }
  }
  const rent = round(rng.range(h.rentYearly[0], h.rentYearly[1]), 10_000);
  const home = { id: h.id, label: h.label, area, rentYearly: rent, rentDueMonths: rent ? rng.int(1, 11) : 0, note: h.note };
  // you study in the city you live in (a Chicago life doesn't commute to Berkeley)
  const g = c.areaGroups?.find((re) => re.test(home.area));
  if (place && g && !g.test(place)) { const near = (affordable.length ? affordable : schools).filter((n) => g.test(n)); if (near.length) place = rng.pick(near); }

  // people: family shares your surname and naming tradition, and no two share a first name
  const name = input.name?.trim() || randomName(c, sex, rng);
  const tradition = groupOf(c, name, rng), sf = !!tradition.surnameFirst;
  const surname = sf ? name.split(' ')[0] : name.split(' ').slice(-1)[0];
  const people: Relation[] = [];
  const used = new Set([sf ? name.split(' ').slice(-1)[0] : name.split(' ')[0]]);
  // a family member's full name in this tradition (`own`: their own family name, e.g. a Chinese mother's)
  const full = (given: string, own = surname) => (sf ? `${own} ${given}` : `${given} ${own}`);
  const first = (sx: Sex) => {
    const pool = tradition[sx].filter((n) => !used.has(n));
    const n = rng.pick(pool.length ? pool : tradition[sx]);
    used.add(n);
    return n;
  };
  // (the game's own family and partner: archived, kin.ts NPC_KIN; the dice still roll so the rest of the life comes out the same)
  const kinFrom = people.length;
  if (rng.chance(stage === 'young' ? 0.95 : 0.82)) people.push({ relation: 'Mother', name: full(first('f'), sf ? rng.pick(tradition.surname.filter((x) => x !== surname)) : surname) });
  if (rng.chance(stage === 'young' ? 0.88 : 0.7)) people.push({ relation: 'Father', name: full(first('m')) });
  const sibs = rng.weighted(c.siblings);
  for (let i = 0; i < sibs; i++) { const sx: Sex = rng.chance(0.5) ? 'm' : 'f'; people.push({ relation: sx === 'm' ? 'Brother' : 'Sister', name: full(first(sx)) }); }
  const other: Sex = sex === 'm' ? 'f' : 'm';
  const partnered = rng.chance(stage === 'young' ? 0.25 : 0.55);
  if (partnered) {
    const married = stage === 'adult' && rng.chance(0.65);
    people.push({ relation: married ? (sex === 'm' ? 'Wife' : 'Husband') : (sex === 'm' ? 'Girlfriend' : 'Boyfriend'), name: randomName(c, other, rng) });
    if (married && rng.chance(0.7)) for (let i = rng.int(1, 3); i > 0; i--) { const sx: Sex = rng.chance(0.5) ? 'm' : 'f'; people.push({ relation: sx === 'm' ? 'Son' : 'Daughter', name: full(first(sx)) }); }
  }
  if (!NPC_KIN) people.splice(kinFrom);
  for (let i = rng.int(2, personality.includes('outgoing') ? 5 : 3); i > 0; i--) people.push({ relation: 'Friend', name: randomName(c, rng.chance(0.6) ? sex : other, rng) });

  const health = Math.round(Math.min(100, 62 + rng.bell() * 40 - (bg.id === 'struggling' ? 6 : 0) + (personality.includes('disciplined') ? 4 : 0)));

  // opportunity: from the whole situation
  const ctx = { stage, edu: level, field: field?.id ?? null, job: job?.id ?? null, bg: bg.id, skills };
  const options = c.opportunities.filter((o) => o.when(ctx));
  const opportunity = options.length ? rng.weighted(options.map((o) => [o.text, o.weight] as const)) : '';

  return {
    seed: input.seed, region: c.id, city: c.city, currency: c.currency, stage, sex, age, name,
    background: { id: bg.id, label: bg.label, family: rng.pick(bg.family) },
    education: { level, label: eduLabel, field: field?.label ?? null, place },
    job: job ? { id: job.id, label: job.label, place: placeFor(nearHome(job.place, home.area), bg.id, rng), incomeMonthly: income } : null,
    // (a floor under the poorest starts in Lagos: a quarter of lives began with too little to eat and get to work on)
    money: c.id === 'lagos' ? Math.max(money, stage === 'young' ? 150_000 : 300_000) : money, home, skills, personality, people, health, opportunity,
  };
}

// Where the job is fits who you are: a wealthy family's child works in Lekki or Ikeja GRA, not Mushin.
const POOR_AREAS = /Mushin|Oshodi|Ajegunle|Ikotun|Bariga|Agege|Ketu|Ikorodu|Alimosho|Ojo/i;
const RICH_AREAS = /Lekki|Ikoyi|Victoria Island|Ikeja GRA|Broad Street|remote/i;
function placeFor(places: string[], bg: string, rng: Rng): string {
  const score = (p: string) => bg === 'wealthy' ? (RICH_AREAS.test(p) ? 3 : POOR_AREAS.test(p) ? 0.05 : 1)
    : bg === 'comfortable' ? (POOR_AREAS.test(p) ? 0.25 : RICH_AREAS.test(p) ? 1.4 : 1)
    : bg === 'struggling' ? (RICH_AREAS.test(p) ? 0.5 : 1) : 1;
  return rng.weighted(places.map((p) => [p, score(p)] as const));
}
